import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '../utils/toast';
import { Plus, Edit, Trash2, Search, X, ChevronDown, ChevronUp, FileQuestion } from 'lucide-react';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { sortObjectOptions } from '@/utils/sortOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuestionBankPageUnified = () => {
  const navigate = useNavigate();
  
  // User state
  const [user, setUser] = useState(null);
  
  // Data states
  const [questions, setQuestions] = useState([]);
  const [trainingTopics, setTrainingTopics] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [topicFilter, setTopicFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Form states - All in one object for easier management
  const [formData, setFormData] = useState({
    selectedTopic: '',
    selectedSection: '',
    questionText: '',
    options: [
      { text: '', is_correct: false },
      { text: '', is_correct: false }
    ],
    answerType: 'mcq_single',
    score: 1,
    primaryLanguage: 'English',
    secondaryLanguages: [], // [{ language: 'Hindi', questionText: '', options: [...] }]
    rightInstruction: '', // Legacy field for backward compatibility
    wrongInstruction: '',  // Legacy field for backward compatibility
    rightInstructionTranslations: { 'English': '', 'Hindi': '' }, // Multilingual right instructions
    wrongInstructionTranslations: { 'English': '', 'Hindi': '' }  // Multilingual wrong instructions
  });
  
  // UI states
  const [showMultilingual, setShowMultilingual] = useState(false);
  const [showLanguageSearch, setShowLanguageSearch] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');

  // Quill editor configuration for instruction fields
  const instructionModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  }), []);

  const instructionFormats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link'
  ];

  useEffect(() => {
    // Initialize user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    fetchLanguages();
    fetchData();
  }, []);

  useEffect(() => {
    // When topic changes, reset section if it's not 'all' or empty
    if (formData.selectedTopic && formData.selectedTopic !== 'all') {
      setFormData(prev => ({ ...prev, selectedSection: '' }));
    }
  }, [formData.selectedTopic]);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/training/languages`);
      setAvailableLanguages(response.data.languages || []);
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      setAvailableLanguages(['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu', 'Marathi', 'Bengali', 'Gujarati', 'Malayalam', 'Punjabi']);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      const [questionsRes, topicsRes] = await Promise.all([
        axios.get(`${API}/assessments/questions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/training/content`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const questionsData = questionsRes.data.questions || questionsRes.data;
      let filteredQuestions = Array.isArray(questionsData) ? questionsData : [];
      let filteredTopics = Array.isArray(topicsRes.data) ? topicsRes.data : [];
      
      // Filter for Hospital Admin: Show ONLY their own questions and topics
      if (userData.role === 'Hospital Admin') {
        filteredQuestions = filteredQuestions.filter(q => q.created_by_id === userData.id);
        filteredTopics = filteredTopics.filter(t => t.created_by_id === userData.id);
      }
      
      setQuestions(filteredQuestions);
      setTrainingTopics(filteredTopics);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error('Fetch error:', error);
      setQuestions([]);
      setTrainingTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, { text: '', is_correct: false }],
        secondaryLanguages: prev.secondaryLanguages.map(secLang => ({
          ...secLang,
          options: [...secLang.options, { text: '', is_correct: false }]
        }))
      }));
    }
  };

  const handleRemoveOption = (index) => {
    if (formData.options.length > 2) {
      setFormData(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index),
        secondaryLanguages: prev.secondaryLanguages.map(secLang => ({
          ...secLang,
          options: secLang.options.filter((_, i) => i !== index)
        }))
      }));
    } else {
      toast.error('At least 2 options required');
    }
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    if (field === 'is_correct') {
      if (formData.answerType === 'mcq_single') {
        // Single correct - uncheck all others
        newOptions.forEach((opt, i) => {
          opt.is_correct = i === index ? value : false;
        });
      } else {
        // Multiple correct
        newOptions[index].is_correct = value;
      }
      
      // Sync correct answers to secondary languages
      const updatedSecondaryLanguages = formData.secondaryLanguages.map(secLang => ({
        ...secLang,
        options: secLang.options.map((opt, i) => ({
          ...opt,
          is_correct: newOptions[i]?.is_correct || false
        }))
      }));
      
      setFormData(prev => ({
        ...prev,
        options: newOptions,
        secondaryLanguages: updatedSecondaryLanguages
      }));
    } else {
      newOptions[index][field] = value;
      setFormData(prev => ({ ...prev, options: newOptions }));
    }
  };

  const handleAddSecondaryLanguage = () => {
    const usedLanguages = [formData.primaryLanguage, ...formData.secondaryLanguages.map(sl => sl.language)];
    const availableToAdd = availableLanguages.filter(lang => !usedLanguages.includes(lang));
    
    if (availableToAdd.length === 0) {
      toast.error('All languages already added');
      return;
    }
    
    const newLanguage = availableToAdd[0];
    const emptyOptions = formData.options.map((opt) => ({
      text: '',
      is_correct: opt.is_correct
    }));
    
    setFormData(prev => ({
      ...prev,
      secondaryLanguages: [
        ...prev.secondaryLanguages,
        { language: newLanguage, questionText: '', options: emptyOptions }
      ]
    }));
  };

  const handleRemoveSecondaryLanguage = (index) => {
    setFormData(prev => ({
      ...prev,
      secondaryLanguages: prev.secondaryLanguages.filter((_, i) => i !== index)
    }));
  };

  const handleSecondaryLanguageChange = (index, field, value) => {
    const updated = [...formData.secondaryLanguages];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, secondaryLanguages: updated }));
  };

  const handleSecondaryOptionChange = (langIndex, optIndex, value) => {
    const updated = [...formData.secondaryLanguages];
    updated[langIndex].options[optIndex].text = value;
    setFormData(prev => ({ ...prev, secondaryLanguages: updated }));
  };

  const resetForm = () => {
    setFormData({
      selectedTopic: '',
      selectedSection: '',
      questionText: '',
      options: [
        { text: '', is_correct: false },
        { text: '', is_correct: false }
      ],
      answerType: 'mcq_single',
      score: 1,
      primaryLanguage: 'English',
      secondaryLanguages: [],
      rightInstruction: '',
      wrongInstruction: '',
      rightInstructionTranslations: { 'English': '', 'Hindi': '' },
      wrongInstructionTranslations: { 'English': '', 'Hindi': '' }
    });
    setEditingQuestion(null);
    setShowMultilingual(false);
  };

  const validateForm = () => {
    // Topic validation
    if (!formData.selectedTopic || formData.selectedTopic === 'all') {
      toast.error('Please select a valid topic');
      return false;
    }
    
    // Subchapter validation
    if (!formData.selectedSubchapter || formData.selectedSubchapter === 'all') {
      toast.error('Please select a valid section');
      return false;
    }
    
    // Question text validation
    if (!formData.questionText || !formData.questionText.trim()) {
      toast.error('Please enter question text');
      return false;
    }
    
    // Options validation
    if (formData.options.length < 2) {
      toast.error('At least 2 options are required');
      return false;
    }
    
    // Check if all options have text
    const emptyOptions = formData.options.filter(opt => !opt.text || !opt.text.trim());
    if (emptyOptions.length > 0) {
      toast.error('All options must have text');
      return false;
    }
    
    // Correct answer validation
    const correctAnswers = formData.options.filter(opt => opt.is_correct);
    if (correctAnswers.length === 0) {
      toast.error('Please mark at least one correct answer');
      return false;
    }
    
    // Multiple choice validation
    if (formData.answerType === 'mcq_single' && correctAnswers.length > 1) {
      toast.error('Single choice questions can only have one correct answer');
      return false;
    }
    
    // Score validation
    if (!formData.score || formData.score < 1) {
      toast.error('Score must be at least 1 point');
      return false;
    }
    
    // Secondary language validation (if any added)
    if (formData.secondaryLanguages.length > 0) {
      for (let i = 0; i < formData.secondaryLanguages.length; i++) {
        const secLang = formData.secondaryLanguages[i];
        if (!secLang.questionText || !secLang.questionText.trim()) {
          toast.error(`Please enter question text for ${secLang.language}`);
          return false;
        }
        
        const emptySecOptions = secLang.options.filter(opt => !opt.text || !opt.text.trim());
        if (emptySecOptions.length > 0) {
          toast.error(`All options must have text for ${secLang.language}`);
          return false;
        }
      }
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem('token');
      
      // Convert secondary languages array to translations dict
      const translationsDict = {};
      formData.secondaryLanguages.forEach(secLang => {
        translationsDict[secLang.language] = {
          question_text: secLang.questionText,
          options: secLang.options.map((opt, idx) => ({
            option_id: `opt-${idx + 1}`,
            text: opt.text,
            is_correct: opt.is_correct
          }))
        };
      });
      
      const payload = {
        topic_id: formData.selectedTopic,
        section_id: formData.selectedSection,
        question_text: formData.questionText,
        question_type: formData.answerType,
        options: formData.options.map((opt, idx) => ({
          option_id: `opt-${idx + 1}`,
          text: opt.text,
          is_correct: opt.is_correct
        })),
        points: parseInt(formData.score),
        score: parseInt(formData.score),
        language: formData.primaryLanguage,
        translations: Object.keys(translationsDict).length > 0 ? translationsDict : null,
        difficulty: "medium",
        auto_grade: true,
        right_instruction: formData.rightInstruction || null, // Legacy field
        wrong_instruction: formData.wrongInstruction || null, // Legacy field
        right_instruction_translations: formData.rightInstructionTranslations,
        wrong_instruction_translations: formData.wrongInstructionTranslations
      };

      if (editingQuestion) {
        await axios.put(`${API}/assessments/questions/${editingQuestion.question_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question updated successfully!');
      } else {
        await axios.post(`${API}/assessments/questions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question created successfully!');
      }

      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      
      // Handle different error formats
      let errorMessage = 'Failed to save question';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // If detail is a string, use it
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // If detail is an array (validation errors)
        else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => 
            typeof err === 'string' ? err : err.msg || JSON.stringify(err)
          ).join(', ');
        }
        // If detail is an object
        else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
        }
        // Fallback to error message if detail not available
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    
    // Handle translations - can be dict or array
    let secondaryLanguages = [];
    if (question.translations) {
      if (Array.isArray(question.translations)) {
        secondaryLanguages = question.translations;
      } else if (typeof question.translations === 'object') {
        // Convert dict to array
        secondaryLanguages = Object.entries(question.translations).map(([lang, data]) => ({
          language: lang,
          questionText: data.question_text || '',
          options: Array.isArray(data.options) ? data.options.map(opt => ({
            text: opt.text || '',
            is_correct: opt.is_correct || false
          })) : []
        }));
      }
    }
    
    const topicId = question.topic_id || question.training_topic_id || '';
    const sectionId = question.section_id || question.training_section_id || '';
    
    console.log('Editing question:', {
      topicId,
      sectionId,
      availableChapters: trainingTopics.length,
      topic: trainingTopics.find(t => t.id === topicId)
    });
    
    // Pre-fill form with existing data
    setFormData({
      selectedTopic: topicId,
      selectedSection: sectionId,
      questionText: question.question_text || '',
      options: Array.isArray(question.options) ? question.options.map(opt => ({
        text: opt.text || '',
        is_correct: opt.is_correct || false
      })) : [
        { text: '', is_correct: false },
        { text: '', is_correct: false }
      ],
      answerType: question.question_type || 'mcq_single',
      score: question.points || question.score || 1,
      primaryLanguage: question.language || question.primary_language || 'English',
      secondaryLanguages: secondaryLanguages,
      rightInstruction: question.right_instruction || '',
      wrongInstruction: question.wrong_instruction || '',
      rightInstructionTranslations: question.right_instruction_translations || { 'English': question.right_instruction || '', 'Hindi': '' },
      wrongInstructionTranslations: question.wrong_instruction_translations || { 'English': question.wrong_instruction || '', 'Hindi': '' }
    });
    
    setShowMultilingual(secondaryLanguages.length > 0);
    setShowAddModal(true);
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assessments/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete question');
    }
  };

  // Filter logic
  const filteredQuestions = questions.filter(q => {
    const qTopicId = q.topic_id || q.training_topic_id;
    const qSectionId = q.section_id || q.training_section_id;
    
    if (topicFilter && topicFilter !== 'all' && qTopicId !== topicFilter) return false;
    if (sectionFilter && sectionFilter !== 'all' && qSectionId !== sectionFilter) return false;
    if (searchText && !q.question_text.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  // Memoize computed values to prevent unnecessary re-renders
  const selectedTopicData = useMemo(
    () => trainingTopics.find(t => t.id === formData.selectedTopic),
    [trainingTopics, formData.selectedTopic]
  );
  
  const availableSections = useMemo(
    () => selectedTopicData?.sections || [],
    [selectedTopicData]
  );
  
  // Memoize selected subchapter name to prevent re-computation on every render
  const selectedSectionName = useMemo(() => {
    if (!formData.selectedSection) return null;
    const section = availableSections.find(s => s.id === formData.selectedSection);
    if (section) return section.section_title;
    
    // If editing and section not found in availableSections, try to get from editingQuestion
    if (editingQuestion) {
      const topicId = editingQuestion.topic_id || editingQuestion.training_topic_id;
      const topic = trainingTopics.find(t => t.id === topicId);
      if (topic) {
        const sec = topic.sections?.find(s => s.id === formData.selectedSection);
        if (sec) return sec.section_title;
      }
    }
    return null;
  }, [formData.selectedSection, availableSections, editingQuestion, trainingTopics]);
  
  // Helper function for backwards compatibility
  const getSelectedSectionName = () => selectedSectionName;

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Question Bank</h1>
            <p className="text-gray-600 mt-2">Manage assessment questions for training chapters</p>
            
            {/* Hospital Admin Info Banner */}
            {user?.role === 'Hospital Admin' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3 max-w-3xl">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-amber-800">
                    <strong>Hospital Admin:</strong> You can only view and manage questions for <strong>your own chapters</strong>. Global chapters and questions from other hospitals are not displayed.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Pre-Assessment Notes Button - Only for Exlva Admin and Super Admin */}
            {(user?.role === 'Super Admin' || user?.role === 'Exlva Admin') && (
              <Button
                onClick={() => navigate('/assessments/pre-assessment-notes')}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <FileQuestion className="w-4 h-4 mr-2" />
                Pre-Assessment Notes
              </Button>
            )}
            <Button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Question
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm">Filter by Chapter</Label>
              <Select value={topicFilter} onValueChange={setTopicFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Chapters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Chapters</SelectItem>
                  {sortObjectOptions(trainingTopics, 'title').map(topic => (
                    <SelectItem key={topic.id} value={topic.id}>
                      {topic.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm">Filter by Subchapter</Label>
              <Select value={sectionFilter} onValueChange={setSectionFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Subchapters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subchapters</SelectItem>
                  {topicFilter && sortObjectOptions(trainingTopics.find(t => t.id === topicFilter)?.sections || [], 'section_title').map(section => (
                    <SelectItem key={section.id} value={section.id}>
                      {section.section_title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search questions..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Questions List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500">
              {user?.role === 'Hospital Admin' 
                ? "No questions found for your chapters. Create a training chapter first, then add questions."
                : "No questions found. Click 'Add Question' to create one."
              }
            </p>
            {user?.role === 'Hospital Admin' && trainingTopics.length === 0 && (
              <Button
                onClick={() => navigate('/training/add')}
                variant="outline"
                className="mt-4"
              >
                Create Training Chapter
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredQuestions
              .sort((a, b) => {
                // Sort by created_at descending (most recent first)
                const dateA = new Date(a.created_at || 0);
                const dateB = new Date(b.created_at || 0);
                return dateB - dateA;
              })
              .map((question) => {
                const topicId = question.topic_id || question.training_topic_id;
                const sectionId = question.section_id || question.training_section_id;
                const topic = trainingTopics.find(t => t.id === topicId);
                const section = topic?.sections?.find(s => s.id === sectionId);
                
                // Get available languages
                const primaryLang = question.language || question.primary_language || 'EN';
                const translationLangs = question.translations 
                  ? (Array.isArray(question.translations) 
                      ? question.translations.map(t => t.language)
                      : Object.keys(question.translations))
                  : [];
                const allLanguages = [primaryLang, ...translationLangs];
                
                // Convert to language codes
                const getLanguageCode = (lang) => {
                  const codes = {
                    'English': 'EN', 'Hindi': 'HI', 'Kannada': 'KN', 'Tamil': 'TA',
                    'Telugu': 'TE', 'Marathi': 'MR', 'Bengali': 'BN', 'Gujarati': 'GU',
                    'Malayalam': 'ML', 'Punjabi': 'PA', 'Odia': 'OR', 'Assamese': 'AS'
                  };
                  return codes[lang] || lang.substring(0, 2).toUpperCase();
                };
                
                return (
                  <Card key={question.question_id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                            {topic?.title || 'Unknown Topic'}
                          </span>
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                            {section?.section_title || 'Unknown Section'}
                          </span>
                          <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                            {question.question_type === 'mcq_single' ? 'Single Choice' : 'Multiple Choice'}
                          </span>
                          <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded">
                            {question.points || question.score || 1} {(question.points || question.score || 1) === 1 ? 'Point' : 'Points'}
                          </span>
                          
                          {/* Language Indicators */}
                          <div className="flex items-center space-x-1">
                            {allLanguages.map((lang, idx) => (
                              <span 
                                key={idx} 
                                className="px-1.5 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-700 rounded border border-indigo-200"
                                data-tooltip={lang}
                              >
                                {getLanguageCode(lang)}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-900 font-medium mb-2">{question.question_text}</p>
                        <div className="space-y-1">
                          {question.options?.map((opt, idx) => (
                            <div key={idx} className="flex items-center space-x-2 text-sm">
                              <span className={`${opt.is_correct ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
                                {opt.is_correct ? '✓' : '○'} {opt.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          onClick={() => handleEdit(question)}
                          variant="outline"
                          size="sm"
                          data-tooltip="Edit Question"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(question.question_id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          data-tooltip="Delete Question"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
          </div>
        )}

        {/* Unified Add/Edit Question Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {editingQuestion ? 'Edit Question' : 'Add New Question'}
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0 mt-4">
            <div className="space-y-6">
              {/* Subchapter 1: Topic & Subchapter Selection */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Topic & Section</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Training Chapter *</Label>
                    <Select 
                      value={formData.selectedTopic} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, selectedTopic: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select topic" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortObjectOptions(trainingTopics, 'title').map(topic => (
                          <SelectItem key={topic.id} value={topic.id}>
                            {topic.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Training Subchapter *</Label>
                    <Select 
                      key={`section-select-${formData.selectedTopic || 'none'}`}
                      value={formData.selectedSection} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, selectedSection: value }))}
                      disabled={!formData.selectedTopic || formData.selectedTopic === 'all' || availableSections.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={
                          !formData.selectedTopic || formData.selectedTopic === 'all' 
                            ? "Select chapter first" 
                            : availableSections.length === 0 
                              ? "No sections available"
                              : "Select section"
                        }>
                          {selectedSectionName || null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availableSections.length === 0 ? (
                          <div className="px-2 py-1 text-sm text-gray-500">No sections available for this chapter</div>
                        ) : (
                          sortObjectOptions(availableSections, 'section_title').map(section => (
                            <SelectItem key={section.id} value={section.id}>
                              {section.section_title}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {formData.selectedSubchapter && !selectedSectionName && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Subchapter ID selected but not found in current chapter
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Subchapter 2: Question Details */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Question Details</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Question Text *</Label>
                    <Textarea
                      placeholder="Enter your question here..."
                      value={formData.questionText}
                      onChange={(e) => setFormData(prev => ({ ...prev, questionText: e.target.value }))}
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Language</Label>
                      <Select 
                        value={formData.primaryLanguage} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, primaryLanguage: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableLanguages.map(lang => (
                            <SelectItem key={lang} value={lang}>
                              {lang}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Answer Type</Label>
                      <Select 
                        value={formData.answerType} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, answerType: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mcq_single">Single Choice</SelectItem>
                          <SelectItem value="mcq_multiple">Multiple Choice</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Score Points</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.score}
                        onChange={(e) => setFormData(prev => ({ ...prev, score: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Subchapter 3: Options with Correct Answer Indicators */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Answer Options 
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    ({formData.answerType === 'mcq_single' ? 'Select one correct' : 'Select one or more correct'})
                  </span>
                </h3>
                <div className="space-y-3">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      {/* Correct Answer Indicator */}
                      <div className="flex-shrink-0">
                        {formData.answerType === 'mcq_single' ? (
                          <input
                            type="radio"
                            name="correct-answer"
                            checked={option.is_correct}
                            onChange={(e) => handleOptionChange(index, 'is_correct', e.target.checked)}
                            className="w-5 h-5 text-green-600 cursor-pointer"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={option.is_correct}
                            onChange={(e) => handleOptionChange(index, 'is_correct', e.target.checked)}
                            className="w-5 h-5 text-green-600 cursor-pointer"
                          />
                        )}
                      </div>

                      {/* Option Text */}
                      <Input
                        placeholder={`Option ${index + 1}`}
                        value={option.text}
                        onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                        className="flex-1"
                      />

                      {/* Remove Button */}
                      <Button
                        onClick={() => handleRemoveOption(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={formData.options.length <= 2}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}

                  {formData.options.length < 6 && (
                    <Button
                      onClick={handleAddOption}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Option
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.answerType === 'mcq_single' 
                    ? '☑ Use radio buttons to select the one correct answer'
                    : '☑ Use checkboxes to select one or more correct answers'}
                </p>
              </div>

              {/* Subchapter 4: Explanation Messages (Right/Wrong Instructions) - Multilingual */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Explanation Messages (Optional)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Provide feedback messages in multiple languages that will be shown to staff/managers based on their answer
                </p>
                
                <div className="space-y-6">
                  {/* Right Instruction - Multilingual */}
                  <div>
                    <Label className="text-sm font-medium text-green-700 mb-3 block">
                      Right Instruction (Shown for Correct Answer)
                    </Label>
                    
                    {/* Language Tabs */}
                    <div className="flex gap-2 mb-3">
                      {['English', 'Hindi'].map((lang) => (
                        <button
                          key={`right-${lang}`}
                          type="button"
                          onClick={() => {/* Tab switching handled by individual editors */}}
                          className="px-3 py-1 text-sm font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200"
                        >
                          {lang}
                        </button>
                      ))}
                    </div>

                    {/* English Right Instruction */}
                    <div className="mb-3">
                      <Label className="text-xs text-gray-600 mb-1 block">English</Label>
                      <div className="border border-green-200 rounded-lg bg-green-50">
                        <ReactQuill
                          theme="snow"
                          value={formData.rightInstructionTranslations.English}
                          onChange={(value) => setFormData(prev => ({ 
                            ...prev, 
                            rightInstructionTranslations: { ...prev.rightInstructionTranslations, English: value }
                          }))}
                          placeholder="Enter explanation for correct answer in English"
                          modules={instructionModules}
                          formats={instructionFormats}
                          className="bg-white"
                          style={{ minHeight: '100px' }}
                        />
                      </div>
                    </div>

                    {/* Hindi Right Instruction */}
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Hindi (हिंदी)</Label>
                      <div className="border border-green-200 rounded-lg bg-green-50">
                        <ReactQuill
                          theme="snow"
                          value={formData.rightInstructionTranslations.Hindi}
                          onChange={(value) => setFormData(prev => ({ 
                            ...prev, 
                            rightInstructionTranslations: { ...prev.rightInstructionTranslations, Hindi: value }
                          }))}
                          placeholder="सही उत्तर के लिए स्पष्टीकरण दर्ज करें"
                          modules={instructionModules}
                          formats={instructionFormats}
                          className="bg-white"
                          style={{ minHeight: '100px' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Wrong Instruction - Multilingual */}
                  <div>
                    <Label className="text-sm font-medium text-red-700 mb-3 block">
                      Wrong Instruction (Shown for Incorrect Answer)
                    </Label>
                    
                    {/* Language Tabs */}
                    <div className="flex gap-2 mb-3">
                      {['English', 'Hindi'].map((lang) => (
                        <button
                          key={`wrong-${lang}`}
                          type="button"
                          onClick={() => {/* Tab switching handled by individual editors */}}
                          className="px-3 py-1 text-sm font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200"
                        >
                          {lang}
                        </button>
                      ))}
                    </div>

                    {/* English Wrong Instruction */}
                    <div className="mb-3">
                      <Label className="text-xs text-gray-600 mb-1 block">English</Label>
                      <div className="border border-red-200 rounded-lg bg-red-50">
                        <ReactQuill
                          theme="snow"
                          value={formData.wrongInstructionTranslations.English}
                          onChange={(value) => setFormData(prev => ({ 
                            ...prev, 
                            wrongInstructionTranslations: { ...prev.wrongInstructionTranslations, English: value }
                          }))}
                          placeholder="Enter explanation for incorrect answer in English"
                          modules={instructionModules}
                          formats={instructionFormats}
                          className="bg-white"
                          style={{ minHeight: '100px' }}
                        />
                      </div>
                    </div>

                    {/* Hindi Wrong Instruction */}
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Hindi (हिंदी)</Label>
                      <div className="border border-red-200 rounded-lg bg-red-50">
                        <ReactQuill
                          theme="snow"
                          value={formData.wrongInstructionTranslations.Hindi}
                          onChange={(value) => setFormData(prev => ({ 
                            ...prev, 
                            wrongInstructionTranslations: { ...prev.wrongInstructionTranslations, Hindi: value }
                          }))}
                          placeholder="गलत उत्तर के लिए स्पष्टीकरण दर्ज करें"
                          modules={instructionModules}
                          formats={instructionFormats}
                          className="bg-white"
                          style={{ minHeight: '100px' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subchapter 5: Multilingual Support (Collapsible) */}
              <div>
                <Button
                  onClick={() => setShowMultilingual(!showMultilingual)}
                  variant="outline"
                  className="w-full justify-between"
                >
                  <span>Multilingual Support (Optional)</span>
                  {showMultilingual ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>

                {showMultilingual && (
                  <div className="mt-4 space-y-4 p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">Add question translations in other languages</p>
                      <Button
                        onClick={handleAddSecondaryLanguage}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Language
                      </Button>
                    </div>

                    {formData.secondaryLanguages.map((secLang, langIndex) => (
                      <div key={langIndex} className="p-4 bg-white border rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <Select
                            value={secLang.language}
                            onValueChange={(value) => handleSecondaryLanguageChange(langIndex, 'language', value)}
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableLanguages
                                .filter(lang => 
                                  lang === secLang.language || 
                                  (lang !== formData.primaryLanguage && 
                                   !formData.secondaryLanguages.some((sl, idx) => idx !== langIndex && sl.language === lang))
                                )
                                .map(lang => (
                                  <SelectItem key={lang} value={lang}>
                                    {lang}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <Button
                            onClick={() => handleRemoveSecondaryLanguage(langIndex)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>

                        <div>
                          <Label className="text-sm">Question Text ({secLang.language})</Label>
                          <Textarea
                            placeholder={`Enter question in ${secLang.language}...`}
                            value={secLang.questionText}
                            onChange={(e) => handleSecondaryLanguageChange(langIndex, 'questionText', e.target.value)}
                            rows={2}
                          />
                        </div>

                        <div>
                          <Label className="text-sm">Options ({secLang.language})</Label>
                          <div className="space-y-2 mt-2">
                            {secLang.options.map((opt, optIndex) => (
                              <Input
                                key={optIndex}
                                placeholder={`Option ${optIndex + 1} in ${secLang.language}`}
                                value={opt.text}
                                onChange={(e) => handleSecondaryOptionChange(langIndex, optIndex, e.target.value)}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            ✓ Correct answers are synced with primary language
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Single Submit Button */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <Button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {editingQuestion ? 'Update Question' : 'Add Question'}
              </Button>
            </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default QuestionBankPageUnified;
